import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Camera, CheckCircle, XCircle, ArrowLeft, ArrowRight,
  ArrowUp, ArrowDown, Smile, Eye, User, RefreshCw,
  ChevronDown, AlertTriangle, Shield, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for challenges
const CHALLENGE_ICONS = {
  turn_left: ArrowLeft,
  turn_right: ArrowRight,
  smile: Smile,
  blink: Eye,
  nod: ChevronDown,
  look_up: ArrowUp,
  look_down: ArrowDown,
  neutral: User
};

export default function LivenessCapture({ onComplete, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [results, setResults] = useState([]);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          }
        });
        
        streamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            setCameraReady(true);
          };
        }
      } catch (err) {
        console.error('Camera access error:', err);
        setError('Camera access denied. Please allow camera access to continue.');
      }
    };

    initCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Start session
  const startSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/verify/liveness/start`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setCurrentChallenge(data.current_challenge);
        setResults([]);
        setFinalResult(null);
        setError(null);
        toast.success('Liveness session started');
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to start session');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Capture frame
  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Mirror the image for selfie view
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.8);
    });
  }, []);

  // Verify challenge
  const verifyChallenge = async () => {
    if (!session || !currentChallenge || isVerifying) return;

    setIsCapturing(true);
    setCountdown(3);

    // Countdown
    for (let i = 3; i > 0; i--) {
      setCountdown(i);
      await new Promise(r => setTimeout(r, 1000));
    }
    setCountdown(null);

    // Capture and verify
    setIsVerifying(true);
    try {
      const blob = await captureFrame();
      if (!blob) {
        throw new Error('Failed to capture frame');
      }

      const formData = new FormData();
      formData.append('frame', blob, 'frame.jpg');

      const res = await fetch(
        `${API_URL}/api/verify/liveness/verify/${session.session_id}/${currentChallenge.index}`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: formData
        }
      );

      if (res.ok) {
        const data = await res.json();
        
        // Add to results
        setResults(prev => [...prev, {
          challenge: currentChallenge.challenge,
          passed: data.challenge_passed,
          confidence: data.confidence
        }]);

        if (data.challenge_passed) {
          toast.success(`Challenge passed! (${data.confidence}% confidence)`);
        } else {
          toast.error('Challenge not detected. Try again on next attempt.');
        }

        if (data.session_complete) {
          setFinalResult(data.verification_result);
          setCurrentChallenge(null);
          
          if (data.verification_result?.is_verified) {
            toast.success('Liveness verification successful!');
          } else {
            toast.error('Liveness verification failed. Please try again.');
          }
        } else if (data.next_challenge) {
          setCurrentChallenge(data.next_challenge);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Verification error. Please try again.');
    } finally {
      setIsCapturing(false);
      setIsVerifying(false);
    }
  };

  // Cancel session
  const cancelSession = async () => {
    if (session) {
      try {
        await fetch(`${API_URL}/api/verify/liveness/cancel/${session.session_id}`, {
          method: 'POST',
          headers: getHeaders()
        });
      } catch (err) {
        console.error('Cancel error:', err);
      }
    }
    
    setSession(null);
    setCurrentChallenge(null);
    setFinalResult(null);
    
    if (onCancel) onCancel();
  };

  // Handle completion
  const handleComplete = () => {
    if (onComplete && finalResult) {
      onComplete(finalResult);
    }
  };

  const ChallengeIcon = currentChallenge ? CHALLENGE_ICONS[currentChallenge.challenge] : User;
  const progress = session ? ((results.length) / session.total_challenges) * 100 : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-400" />
            Liveness Verification
          </CardTitle>
          <CardDescription className="text-gray-400">
            Follow the on-screen instructions to verify you are a real person
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <div className="flex items-center gap-2 text-red-300">
                <AlertTriangle className="h-5 w-5" />
                {error}
              </div>
            </div>
          )}

          {/* Camera View */}
          <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover transform scale-x-[-1]"
              data-testid="liveness-video"
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                <div className="text-center text-gray-400">
                  <Camera className="h-12 w-12 mx-auto mb-2 animate-pulse" />
                  <p>Initializing camera...</p>
                </div>
              </div>
            )}

            {countdown && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="text-8xl font-bold text-white animate-pulse">
                  {countdown}
                </div>
              </div>
            )}

            {/* Oval guide overlay */}
            <div className="absolute inset-0 pointer-events-none">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <mask id="faceMask">
                    <rect width="100" height="100" fill="white" />
                    <ellipse cx="50" cy="45" rx="25" ry="35" fill="black" />
                  </mask>
                </defs>
                <rect width="100" height="100" fill="rgba(0,0,0,0.4)" mask="url(#faceMask)" />
                <ellipse cx="50" cy="45" rx="25" ry="35" fill="none" stroke="rgba(0,255,136,0.5)" strokeWidth="0.5" strokeDasharray="2,2" />
              </svg>
            </div>
          </div>

          {/* Progress */}
          {session && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Progress</span>
                <span>{results.length} / {session.total_challenges} challenges</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Current Challenge */}
          {currentChallenge && !finalResult && (
            <div className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-xl text-center">
              <ChallengeIcon className="h-16 w-16 mx-auto mb-4 text-purple-300" />
              <h3 className="text-2xl font-bold text-white mb-2">
                {currentChallenge.instruction}
              </h3>
              <p className="text-gray-300 mb-4">
                {currentChallenge.description}
              </p>
              <Badge variant="outline" className="text-purple-300 border-purple-500">
                {currentChallenge.duration_seconds} seconds
              </Badge>
            </div>
          )}

          {/* Final Result */}
          {finalResult && (
            <div className={`p-6 rounded-xl text-center ${
              finalResult.is_verified 
                ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-700' 
                : 'bg-gradient-to-r from-red-900/50 to-pink-900/50 border border-red-700'
            }`}>
              {finalResult.is_verified ? (
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-400" />
              ) : (
                <XCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
              )}
              <h3 className="text-2xl font-bold text-white mb-2">
                {finalResult.is_verified ? 'Verification Successful!' : 'Verification Failed'}
              </h3>
              <p className="text-gray-300 mb-4">
                {finalResult.is_verified 
                  ? 'Your identity has been verified.'
                  : 'Please try again with better lighting and positioning.'}
              </p>
              <div className="flex justify-center gap-4 text-sm">
                <Badge className={finalResult.is_verified ? 'bg-green-600' : 'bg-red-600'}>
                  {finalResult.pass_rate}% Pass Rate
                </Badge>
                <Badge variant="outline" className="text-gray-300">
                  {finalResult.challenges_passed} / {finalResult.total_challenges} Passed
                </Badge>
              </div>
            </div>
          )}

          {/* Results History */}
          {results.length > 0 && (
            <div className="flex gap-2 flex-wrap justify-center">
              {results.map((r, idx) => (
                <Badge 
                  key={idx}
                  className={r.passed ? 'bg-green-600' : 'bg-red-600'}
                >
                  {r.passed ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                  {r.challenge.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center">
            {!session && !finalResult && (
              <Button
                onClick={startSession}
                disabled={!cameraReady}
                className="bg-green-600 hover:bg-green-700"
                data-testid="start-liveness-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Start Verification
              </Button>
            )}

            {session && currentChallenge && !finalResult && (
              <>
                <Button
                  onClick={verifyChallenge}
                  disabled={isCapturing || isVerifying || !cameraReady}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="capture-btn"
                >
                  {isVerifying ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4 mr-2" />
                  )}
                  {isVerifying ? 'Verifying...' : 'Capture'}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelSession}
                  className="border-red-600 text-red-400 hover:bg-red-900/30"
                >
                  Cancel
                </Button>
              </>
            )}

            {finalResult && (
              <>
                {finalResult.is_verified ? (
                  <Button
                    onClick={handleComplete}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="complete-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setSession(null);
                      setCurrentChallenge(null);
                      setFinalResult(null);
                      setResults([]);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Instructions */}
          {!session && !finalResult && (
            <div className="text-center text-gray-400 text-sm space-y-1">
              <p>Position your face in the oval guide</p>
              <p>Ensure good lighting and a plain background</p>
              <p>You will be asked to perform {3}-{5} random actions</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
